import { InMemoryCache, makeVar } from '@apollo/client';
import { Localization } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ru';
import ReactDOM from 'react-dom';
import { ReactNotifications } from 'react-notifications-component';
import { HashRouter } from 'react-router-dom';

import { Auth } from 'auth';
import { Cashbox } from 'cashbox';
import { Features } from 'features';
import {
  initializeLanguagePreference,
  LocalizationContext,
  LOCALES_MAP,
} from 'localization';
import { NotCompletedProvider } from 'modals/ClientRegisterModal';
import { purgeSandboxNotCompletedData } from 'modals/ClientRegisterModal/notCompletedStorage';
import { TariffHOC } from 'pages/Tariffs/TariffContext';
import { installSandboxNetworkGuard, isSandbox } from 'sandbox';
import { ErrorHandler } from 'services/ErrorHandler';
import { SettingsProvider } from 'settings';

import 'react-notifications-component/dist/theme.css';
import '@smartshell/ui-kit/dist/styles.css';
import './style.scss';

import { CustomApolloProvider } from './apollo';
import App from './App';
import { ThemeProvider } from './theme';
import { PrintProvider } from './utils';

dayjs.extend(localeData);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

window.localStorage.removeItem('apollo-cache-persist');
initializeLanguagePreference(
  window.localStorage,
  navigator.languages?.length ? navigator.languages : [navigator.language]
);
purgeSandboxNotCompletedData(window.localStorage, isSandbox);
installSandboxNetworkGuard();

// TODO: ws turn on before release
// if (process.env.NODE_ENV === 'development') {
//   const metaTag = document.createElement('meta');
//   metaTag.httpEquiv = 'Content-Security-Policy';
//   metaTag.content = 'connect-src ws: *';
//   document.head.appendChild(metaTag);
// }
export const clientsReloadTrigger = makeVar<number>(0);

const cache = new InMemoryCache({
  typePolicies: {
    User: {
      keyFields: ['uuid'],
    },
    UserPaginated: {
      merge: true,
    },
    Query: {
      fields: {
        hostOverview: {
          read: (_, { args, toReference }) =>
            toReference({ __typename: 'HostOverview', id: args?.id }),
        },
        hostGroupOverview: {
          read: (_, { args, toReference }) =>
            toReference({ __typename: 'HostGroupOverview', id: args?.id }),
        },
        clientsReloadTrigger: {
          read() {
            return clientsReloadTrigger();
          },
        },
        settingValues: {
          keyArgs: false,

          merge(_, incoming) {
            return [...incoming];
          },
        },
      },
    },
    SettingValue: {
      keyFields: ['alias'],
    },
  },
});

ReactDOM.render(
  <ErrorHandler>
    <Localization
      context={LocalizationContext}
      locales={LOCALES_MAP}
      useKeyboardHandler
    >
      <PrintProvider>
        <ThemeProvider>
          <CustomApolloProvider cache={cache}>
            <HashRouter>
              <Auth>
                <Features>
                  <SettingsProvider>
                    <Cashbox>
                      <TariffHOC>
                        <NotCompletedProvider sandbox={isSandbox}>
                          <ReactNotifications />
                          <App />
                        </NotCompletedProvider>
                      </TariffHOC>
                    </Cashbox>
                  </SettingsProvider>
                </Features>
              </Auth>
            </HashRouter>
          </CustomApolloProvider>
        </ThemeProvider>
      </PrintProvider>
    </Localization>
  </ErrorHandler>,
  document.getElementById('root')
);
